import client from './client';

export type EditorOp =
  | { op: 'render.preset'; presetKey: string; presetName: string; settings: { environment?: Record<string, unknown>; effects?: Record<string, unknown> }; reason?: string }
  | { op: 'model.visible'; modelId: string; visible: boolean; reason?: string }
  | { op: 'model.lock'; modelId: string; locked: boolean; reason?: string }
  | { op: 'model.transform'; modelId: string; position?: number[]; rotation?: number[]; scale?: number[]; reason?: string }
  | { op: 'model.remove'; modelId: string; reason?: string }
  | { op: 'model.duplicate'; modelId: string; reason?: string }
  | { op: 'model.materialColor'; modelId: string; color: string; reason?: string }
  | { op: 'model.bind'; modelId: string; visualType: string; deviceId?: string; bindPointKey?: string; ruleConfig?: Record<string, unknown>; reason?: string }
  | {
      /** A4：换基座（服务端已按 manifest 展开为模型规格；替换场景全部模型） */
      op: 'base.load';
      baseId: string;
      baseName?: string;
      models: Array<{
        name: string;
        url: string;
        format?: string;
        position?: number[];
        rotation?: number[];
        scale?: number[];
        visible?: boolean;
        locked?: boolean;
        presetId?: string | null;
        materials?: unknown;
      }>;
      reason?: string;
    };

export interface EditorPlanResponse {
  callId: string;
  summary: string;
  ops: Array<EditorOp | DashboardOp>;
  source: 'llm' | 'heuristic';
}

export interface AgentStep {
  index: number;
  title: string;
  reason?: string;
  ops: Array<EditorOp | DashboardOp>;
}

export interface AgentPlanResponse {
  callId: string;
  summary: string;
  steps: AgentStep[];
  source: 'llm' | 'heuristic';
  target: 'scene' | 'dashboard';
  mode: 'agent';
}

export interface EditorModelContext {
  id: string;
  name: string;
  visible?: boolean;
  locked?: boolean;
}

// ── E2：大屏操作 ──

export interface WidgetComponentSpec {
  type: string;
  size?: { width: number; height: number };
  base?: Record<string, unknown>;
  special?: Record<string, unknown>;
  style?: Record<string, unknown>;
  config?: Record<string, unknown>;
}

export type DashboardOp =
  | { op: 'dashboard.theme'; themeKey: string; themeName: string; pageConfig: Record<string, unknown>; reason?: string }
  | { op: 'widget.add'; component: WidgetComponentSpec; reason?: string }
  | { op: 'widget.replace'; componentId: string; component: WidgetComponentSpec; reason?: string }
  | { op: 'widget.update'; componentId: string; patch: Record<string, unknown>; reason?: string }
  | { op: 'widget.bind'; componentId: string; dataSetId?: string; deviceId?: string; pointKey?: string; reason?: string }
  | { op: 'widget.remove'; componentId: string; reason?: string };

export interface DashboardComponentContext {
  id: string;
  type: string;
  title?: string;
}

export async function planAiEditor(body: {
  prompt: string;
  target?: 'scene' | 'dashboard';
  scope: 'global' | 'selection' | 'region';
  mode?: 'single' | 'agent';
  sceneName?: string;
  selectedModelId?: string;
  /** G2：region 模式白名单 */
  modelIds?: string[];
  models?: EditorModelContext[];
  components?: DashboardComponentContext[];
  datasets?: Array<{ id: string; name: string }>;
  pageConfig?: { width?: number; height?: number; backgroundColor?: string };
  selectedComponentId?: string;
}): Promise<EditorPlanResponse | AgentPlanResponse> {
  const { data } = await client.post('/ai/editor/plan', body);
  return data;
}

/** G1：Agent 多步计划（返回 steps） */
export async function planAiEditorAgent(body: {
  prompt: string;
  scope: 'global' | 'selection' | 'region';
  sceneName?: string;
  selectedModelId?: string;
  modelIds?: string[];
  models: EditorModelContext[];
}): Promise<AgentPlanResponse> {
  const { data } = await client.post('/ai/editor/plan', { ...body, mode: 'agent' });
  return data as AgentPlanResponse;
}

export async function markAiEditorStatus(
  callId: string,
  body: { status: 'applied' | 'discarded' | 'failed'; error?: string },
): Promise<{ ok: boolean; status: string }> {
  const { data } = await client.post(`/ai/editor/${callId}/status`, body);
  return data;
}
